import { Injectable } from '@angular/core';
import { SolType } from '../models/core/sol-type';

@Injectable({
  providedIn: 'root',
})
export class TypeService {
  private readonly types = new Map<string, SolType<any>>();

  public addType(type: SolType<any>): void {
    if (this.types.has(type.id)) {
      throw new Error(`Duplicate type registration: ${type.id}`);
    }
    this.types.set(type.id, type);
  }

  public byId(id: string): SolType<any> | undefined {
    const direct = this.types.get(id);
    if (direct !== undefined) {
      return direct;
    }
    if (id.endsWith('[]')) {
      const base = this.byId(id.slice(0, -2));
      return base?.array;
    }
    return undefined;
  }

  /**
   * Looks up a registered `SolType` by the id a descriptor advertises, `[]` suffix included.
   * Throws when nothing is registered under it: rendering an unknown type as text loses whichever
   * editor and validation it carried, so a payload error is surfaced rather than absorbed.
   */
  public resolveType(id: string): SolType<any> {
    const type = this.byId(id);
    if (type === undefined) {
      throw new Error(`No registered SolType with id '${id}'.`);
    }
    return type;
  }

  /**
   * Creates a TypeService pre-loaded with all built-in SolTypes.
   * Useful for testing without Angular DI.
   */
  public static withBuiltInTypes(): TypeService {
    const service = new TypeService();
    const builtIns = [
      SolType.any,
      SolType.string,
      SolType.text,
      SolType.number,
      SolType.boolean,
      SolType.date,
      SolType.icon,
      SolType.tag,
      SolType.trend,
      SolType.severity,
      SolType.button,
      SolType.log,
      SolType.budget,
      SolType.fileUpload,
      SolType.image,
    ];
    for (const type of builtIns) {
      service.addType(type);
    }
    return service;
  }
}
